import React, { Fragment } from 'react';
import moment from 'moment';
import classnames from 'classnames';

import { noop } from 'buyside/util/lodashAlt';

import styles from './RankIndexStyles';

import DownArrowIcon from 'buyside/svgs/down_arrow.svg';
import PaginatedIndex, {
  PaginatedIndexItems,
  PaginationButton
} from 'buyside/components/presentation/Index/paginatedIndex';
import RankCard from '../rankCard/RankCard';
import RankingIndexJoyride from 'buyside/components/presentation/Joyride/RankingIndexJoyride/rankingIndexJoyride';
import { formatCategory } from 'buyside/util/rankCategoryHelper';
const { rankingCategories } = require('buyside/util/constantsFromRails.json');

const RankIndexContext = React.createContext();

const CustomPaginationButton = ({ fetchNextPage, isPublic }) => {
  return (
    <div
      className={classnames(styles.paginationButton, { [styles.disabled]: isPublic })}
      onClick={isPublic ? noop : fetchNextPage}
    >
      <span>
        <DownArrowIcon
          className={classnames(styles.paginationButtonDownArrow, { [styles.disabled]: isPublic })}
        />
        Load More Rankings
      </span>
    </div>
  );
};

export const rankRenderer = (rank, idx, isCurrentUserCard = false) => {
  return (
    <div key={idx}>
      <RankIndexContext.Consumer>
        {({ canViewIdeaFilterDetails, canViewUserDetails, category, isPublic, showIdeasLink }) => (
          <RankCard
            badgeRelationships={rank.user.badgeCollectionRelationships.edges
              .map(badgeRelationShip => badgeRelationShip.node)
              .reverse()}
            canViewUserDetails={canViewUserDetails}
            category={category}
            isCurrentUserCard={isCurrentUserCard}
            isFirstRankCard={idx === 0}
            isPublic={isPublic}
            rank={rank}
            showIdeaDetails={canViewIdeaFilterDetails}
            showIdeasLink={showIdeasLink}
            topIdeas={rank.user.topIdeas.edges.map(idea => idea.node)}
          />
        )}
      </RankIndexContext.Consumer>
    </div>
  );
};

export const generateDefaultSubtitle = rankingCreatedDate => {
  return rankingCreatedDate
    ? `${moment(rankingCreatedDate).format('MMMM')} ${moment(rankingCreatedDate).year()}`
    : '';
};

export const TitleCard = ({ title, subtitle }) => {
  return (
    <RankIndexContext.Consumer>
      {context => {
        const { category, rankingCreatedDate } = context;
        const defaultSubtitle = generateDefaultSubtitle(rankingCreatedDate);
        let defaultTitle = formatCategory(category);
        if (defaultSubtitle.length) {
          defaultTitle = defaultTitle.concat(' - ');
        }

        return (
          <div className={styles.ranksIndexTitle}>
            {title ? title : defaultTitle}
            <span className={styles.ranksTitleDetail}>{subtitle ? subtitle : defaultSubtitle}</span>
          </div>
        );
      }}
    </RankIndexContext.Consumer>
  );
};

export const RankCards = ({ isPaginated = true, customRankRenderer }) => {
  return (
    <RankIndexContext.Consumer>
      {context => {
        const { ranks, hasNextPage, fetchNextPage, isBlank, isPublic, category } = context;
        if (isBlank) {
          // render blank index items
          const blankRankCards = Array(10)
            .fill(' ')
            .map((_v, idx) => {
              return (
                <RankCard
                  isBlank
                  key={idx}
                  isBlankOptions={{
                    rankNum: `#${idx + 1}`,
                    rankString: formatCategory(category)
                  }}
                />
              );
            });
          return <Fragment>{blankRankCards}</Fragment>;
        }

        return (
          <PaginatedIndex
            indexItems={ranks}
            indexItemRenderer={customRankRenderer ? customRankRenderer : rankRenderer}
            hasNextPage={isPaginated && hasNextPage}
            fetchNextPage={fetchNextPage}
          >
            <PaginatedIndexItems />
            {category === rankingCategories[0] && <RankingIndexJoyride isPublic={isPublic} />}
            <PaginationButton>
              {({ fetchNextPage }) => (
                <CustomPaginationButton fetchNextPage={fetchNextPage} isPublic={isPublic} />
              )}
            </PaginationButton>
          </PaginatedIndex>
        );
      }}
    </RankIndexContext.Consumer>
  );
};

const RankIndex = ({
  canViewIdeaFilterDetails,
  canViewUserDetails,
  category,
  children,
  fetchNextPage,
  hasNextPage,
  isBlank = false,
  isPublic,
  rankingCreatedDate,
  ranks,
  showIdeasLink
}) => {
  return (
    <RankIndexContext.Provider
      value={{
        canViewIdeaFilterDetails,
        canViewUserDetails,
        category,
        fetchNextPage,
        hasNextPage,
        isBlank,
        isPublic,
        rankingCreatedDate,
        ranks,
        showIdeasLink
      }}
    >
      <div className={styles.ranksContainer}>{children}</div>
    </RankIndexContext.Provider>
  );
};

export default RankIndex;
